import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { GoogleLogin } from "@react-oauth/google";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { linkGoogleAccount } from "api/GoogleAuth";
import { refusalOf } from "api/refusal";
import { FOCUS_RING, TOUCH_TARGET } from "utils/chackra/controlStyles";
import { googleLinkErrorId } from "./linkErrors";

interface GoogleLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  onLinked: () => void;
  /** The dialog has fully gone, exit animation included. */
  onClosed?: () => void;
}

/**
 * Google's own button in a dialog: an id_token cannot be requested from a bare
 * click, and useGoogleLogin yields none. Closing the dialog, or Google's
 * chooser, does nothing and says nothing.
 */
const GoogleLinkModal = ({ isOpen, onClose, userId, onLinked, onClosed }: GoogleLinkModalProps) => {
  const intl = useIntl();
  const toast = useToast();
  const [isLinking, setIsLinking] = useState(false);
  const linkInFlight = useRef(false);
  // Told only once the dialog is gone: the card's refresh then moves focus,
  // which an open dialog's focus lock would pull straight back.
  const linkedOnClose = useRef(false);
  // Google's callback outlives the dialog: a chooser popup can finish after the
  // user closed it, and closing means nothing happens.
  const isOpenNow = useRef(isOpen);
  isOpenNow.current = isOpen;
  // The button and the close button leave while linking; focus waits here.
  const statusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLinking) statusRef.current?.focus();
  }, [isLinking]);

  const report = (id: string, status: "success" | "error") =>
    toast({
      title: intl.formatMessage({ id }),
      status,
      position: "top",
      duration: 5000,
      isClosable: true,
    });

  const handleCredential = async ({ credential }: { credential?: string }) => {
    if (!credential || linkInFlight.current || !isOpenNow.current) return;
    linkInFlight.current = true;
    setIsLinking(true);
    try {
      await linkGoogleAccount(credential, userId);
      report("signInMethods.google.success", "success");
      linkedOnClose.current = true;
    } catch (error) {
      report(googleLinkErrorId(error), "error");
      // The server knows of a link this page does not show yet: catch up.
      if (refusalOf(error).detail === "already_linked_self") linkedOnClose.current = true;
    } finally {
      linkInFlight.current = false;
      setIsLinking(false);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={!isLinking}
      closeOnOverlayClick={!isLinking}
      onCloseComplete={() => {
        onClosed?.();
        if (!linkedOnClose.current) return;
        linkedOnClose.current = false;
        onLinked();
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent bg="bg.overlay" color="text.primary" mx={4}>
        <ModalHeader>{intl.formatMessage({ id: "signInMethods.google.title" })}</ModalHeader>
        {!isLinking && (
          <ModalCloseButton boxSize={TOUCH_TARGET} _focusVisible={FOCUS_RING} />
        )}
        <ModalBody pb={6}>
          {isLinking ? (
            <Flex ref={statusRef} role="status" tabIndex={-1} align="center" gap={2}>
              <Spinner size="sm" aria-hidden="true" />
              <Text>{intl.formatMessage({ id: "signInMethods.google.linking" })}</Text>
            </Flex>
          ) : (
            <>
              <Text mb={4} color="text.secondary">
                {intl.formatMessage({ id: "signInMethods.google.body" })}
              </Text>
              <GoogleLogin onSuccess={handleCredential} locale={intl.locale} />
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default GoogleLinkModal;
